import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { EmptyState } from '@/components/admin/shared/EmptyState';
import { CustomDomainStatusBadge } from '@/components/practitioner/BrandingPage/CustomDomainStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import type { TenantCustomDomain } from '@/api/customDomains/customDomainsApi.types';
import {
  useDisableTenantCustomDomainMutation,
  useEnableTenantCustomDomainMutation,
  useTenantCustomDomainsQuery,
} from '@/api/customDomains/useTenantCustomDomains';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface CustomDomainsTabProps {
  tenantId: string;
}

export function CustomDomainsTab({ tenantId }: CustomDomainsTabProps) {
  const { t } = useTranslation('customDomains');
  const customDomainsQuery = useTenantCustomDomainsQuery(tenantId);
  const disableMutation = useDisableTenantCustomDomainMutation(tenantId);
  const enableMutation = useEnableTenantCustomDomainMutation(tenantId);
  const [pendingCustomDomain, setPendingCustomDomain] = useState<TenantCustomDomain | null>(null);

  if (customDomainsQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (customDomainsQuery.isError === true || customDomainsQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          customDomainsQuery.refetch();
        }}
      />
    );
  }

  const customDomains = customDomainsQuery.data;
  const isMutating = disableMutation.isPending || enableMutation.isPending;

  function closeConfirmation() {
    setPendingCustomDomain(null);
  }

  function notifyActionFailed(actionError: unknown) {
    const backendMessage = resolveApiErrorMessage(actionError);
    showErrorNotification(backendMessage.length > 0 ? backendMessage : t('admin.actionFailed'));
    closeConfirmation();
  }

  function confirmPendingAction() {
    if (pendingCustomDomain === null) {
      return;
    }
    if (pendingCustomDomain.isDisabledByPlatform === true) {
      enableMutation.mutate(pendingCustomDomain.id, {
        onSuccess: function notifyEnabled() {
          showSuccessNotification(t('admin.enabledNotice'));
          closeConfirmation();
        },
        onError: notifyActionFailed,
      });
      return;
    }
    disableMutation.mutate(pendingCustomDomain.id, {
      onSuccess: function notifyDisabled() {
        showSuccessNotification(t('admin.disabledNotice'));
        closeConfirmation();
      },
      onError: notifyActionFailed,
    });
  }

  const confirmationKey =
    pendingCustomDomain !== null && pendingCustomDomain.isDisabledByPlatform === true
      ? 'admin.enableConfirm'
      : 'admin.disableConfirm';

  return (
    <div>
      <h2 className="text-sm font-bold text-zooindigo">{t('admin.title')}</h2>
      <p className="text-xs text-zoomuted mt-1 mb-5">{t('admin.subtitle')}</p>

      {customDomains.length === 0 ? (
        <EmptyState iconName="Globe" title={t('admin.title')} description={t('admin.empty')} />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {customDomains.map(function renderCustomDomain(customDomain) {
            return (
              <Card key={customDomain.id} padding="none" shadow={false} className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Icon name="Globe" className="text-zooviolet" />
                      <h3 className="text-sm font-bold text-zooindigo break-all">
                        {customDomain.displayHostname}
                      </h3>
                      <CustomDomainStatusBadge status={customDomain.status} />
                      {customDomain.isPrimary === true ? (
                        <Badge tone="accent" size="sm">
                          {t('admin.primaryBadge')}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-zoomuted mt-2">
                      <span className="font-bold">{t('admin.reasonLabel')}: </span>
                      {customDomain.statusReason === null
                        ? t('admin.noReason')
                        : t('reasons.' + customDomain.statusReason + '.message')}
                    </p>
                    <p className="text-xs text-zoomuted mt-1">
                      <span className="font-bold">{t('admin.certificateLabel')}: </span>
                      {customDomain.certificateExpiresAt === null
                        ? t('admin.noCertificate')
                        : formatDateToDayMonthYear(parseISO(customDomain.certificateExpiresAt))}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="chip"
                    variant={
                      customDomain.isDisabledByPlatform === true
                        ? 'outlinePrimary'
                        : 'destructiveGhost'
                    }
                    disabled={isMutating || customDomain.status === 'removing'}
                    onClick={function requestAction() {
                      setPendingCustomDomain(customDomain);
                    }}
                  >
                    {customDomain.isDisabledByPlatform === true
                      ? t('admin.enable')
                      : t('admin.disable')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {pendingCustomDomain !== null ? (
        <ConfirmDialog
          tone={pendingCustomDomain.isDisabledByPlatform === true ? 'default' : 'danger'}
          iconName="Globe"
          title={t(confirmationKey + '.title')}
          description={t(confirmationKey + '.description', {
            hostname: pendingCustomDomain.displayHostname,
          })}
          confirmLabel={t(confirmationKey + '.confirm')}
          isConfirming={isMutating}
          onConfirm={confirmPendingAction}
          onCancel={closeConfirmation}
        />
      ) : null}
    </div>
  );
}
